import type { NotesPanel, PuzzleCopy, TextSwap } from "./config";

/**
 * Content fixes applied to a puzzle's own HTML once it has loaded.
 *
 * The puzzle frames are same-origin (served from /api/load_puzzle), so the
 * preview can reach into them. Doing it from here rather than editing the files
 * under payloads/ means the live show is untouched — these are preview-only
 * changes until someone decides otherwise.
 *
 * Two kinds of fix, both declared per-puzzle in ./config.ts:
 *  - `swaps`: literal wording replacements (p6's answer prompt says "I" where it
 *    means Susie).
 *  - `notes`: p5's clue list was baked into the JPEG in a small typewriter face
 *    that goes to mush once the picture is scaled to fit. This lays a panel of
 *    real, selectable, resizable text over that part of the artwork.
 */

const PANEL_ID = "preview-notes";
const SUBMIT_STYLE_ID = "preview-submit";

/**
 * The submit control is authored at 30px in the payload HTML. That's fine in a
 * show a group plays sitting at their own screens, but the preview gets used at
 * arm's length on someone else's machine, so it's given a bigger target and a
 * press affordance here. Preview-only: the show's own layout is untouched.
 */
const SUBMIT_CSS = `
  img[src*="enter.svg"], img[src*="enter-white.svg"] {
    width: 44px !important;
    height: auto !important;
    transition: transform 0.12s ease, filter 0.12s ease;
  }
  a:hover > img[src*="enter.svg"], a:hover > img[src*="enter-white.svg"] {
    transform: scale(1.09);
    filter: drop-shadow(0 3px 8px rgba(0, 0, 0, 0.4));
  }
  a:active > img[src*="enter.svg"], a:active > img[src*="enter-white.svg"] {
    transform: scale(0.96);
  }
`;

/** Add a stylesheet to a puzzle document once, keyed by id. */
function addStyle(doc: Document, id: string, css: string): void {
  doc.getElementById(id)?.remove();
  const style = doc.createElement("style");
  style.id = id;
  style.textContent = css;
  doc.head?.appendChild(style);
}

/** Replace text in a frame, matching whole text nodes' content. */
function applySwaps(doc: Document, swaps: TextSwap[]): void {
  if (!swaps.length) return;
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) nodes.push(n as Text);
  for (const node of nodes) {
    for (const { from, to } of swaps) {
      if (node.nodeValue?.includes(from)) node.nodeValue = node.nodeValue.split(from).join(to);
    }
  }
}

/**
 * Where the picture is actually painted inside its element box. The fit
 * stylesheet gives the image a full-size box and `object-fit: contain`, so the
 * picture is letterboxed within it and the notes have to follow the picture, not
 * the box. Same geometry the magnifier does in public/blowup/blowup.min.js.
 */
function paintedBox(img: HTMLImageElement): { x: number; y: number; w: number; h: number } | null {
  const box = img.getBoundingClientRect();
  const { naturalWidth: nw, naturalHeight: nh } = img;
  if (!box.width || !box.height || !nw || !nh) return null;
  const ratio = Math.min(box.width / nw, box.height / nh);
  const w = nw * ratio;
  const h = nh * ratio;
  return { x: (box.width - w) / 2, y: (box.height - h) / 2, w, h };
}

/** Grow the text to the largest size that still fits the panel. */
function fitText(panel: HTMLElement): void {
  let lo = 7;
  let hi = 42;
  while (hi - lo > 0.5) {
    const mid = (lo + hi) / 2;
    panel.style.fontSize = `${mid}px`;
    if (panel.scrollHeight <= panel.clientHeight) lo = mid;
    else hi = mid;
  }
  panel.style.fontSize = `${lo}px`;
}

function drawNotes(doc: Document, notes: NotesPanel): void {
  const img = doc.querySelector<HTMLImageElement>(".img-magnifier-container > img, #box > img");
  const host = img?.parentElement;
  if (!img || !host) return;

  const place = () => {
    const painted = paintedBox(img);
    if (!painted) return;

    let panel = doc.getElementById(PANEL_ID);
    if (!panel) {
      panel = doc.createElement("div");
      panel.id = PANEL_ID;
      // Taking the pointer here is deliberate: it pulls the cursor off the
      // image, so the magnifier hides rather than lensing the stale baked-in
      // text underneath. The bottles above stay magnifiable.
      panel.style.cssText = [
        "position:absolute",
        "box-sizing:border-box",
        "background:#efece4",
        "color:#15130f",
        "border-radius:2px",
        "box-shadow:0 1px 10px rgba(0,0,0,0.25)",
        "font-family:apple-system,system-ui,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif",
        "line-height:1.5",
        "overflow:hidden",
        "z-index:50",
        "cursor:default",
      ].join(";");

      const list = doc.createElement("div");
      list.id = `${PANEL_ID}-list`;
      for (const line of notes.lines) {
        const p = doc.createElement("p");
        p.textContent = line;
        p.style.cssText = "margin:0 0 0.55em";
        list.appendChild(p);
      }
      if (notes.heading) {
        const h = doc.createElement("p");
        h.textContent = notes.heading;
        h.style.cssText = "margin:0 0 0.8em;font-weight:700";
        list.insertBefore(h, list.firstChild);
      }
      panel.appendChild(list);
      host.appendChild(panel);
    }

    panel.style.left = `${painted.x + notes.left * painted.w}px`;
    panel.style.top = `${painted.y + notes.top * painted.h}px`;
    panel.style.width = `${notes.width * painted.w}px`;
    panel.style.height = `${notes.height * painted.h}px`;
    // Pad proportionally so the panel looks the same at any window size.
    panel.style.padding = `${notes.height * painted.h * 0.055}px ${notes.width * painted.w * 0.035}px`;
    fitText(panel);
  };

  if (img.complete && img.naturalWidth) place();
  else img.addEventListener("load", place, { once: true });

  // Reposition with the window; the picture letterboxes differently at each size.
  doc.defaultView?.addEventListener("resize", place);
}

/**
 * Apply a puzzle's content fixes to one of its frames. Safe to call repeatedly;
 * does nothing if the document isn't reachable.
 */
export function tweakPuzzleFrame(
  frame: HTMLIFrameElement | null,
  copy: PuzzleCopy,
  which: "primary" | "secondary",
): void {
  let doc: Document | null = null;
  try {
    doc = frame?.contentDocument ?? null;
  } catch {
    return; // cross-origin; leave the puzzle as its author wrote it
  }
  if (!doc?.body) return;

  addStyle(doc, SUBMIT_STYLE_ID, SUBMIT_CSS);
  applySwaps(
    doc,
    (copy.swaps ?? []).filter((s) => s.frame === which),
  );
  if (which === "primary" && copy.notes) drawNotes(doc, copy.notes);
}
